export const virtualReceptionistAdvantages = [
  {
    icon: 'PhoneCall',
    title: 'Instant pickup with zero hold times',
    description: 'Answer every caller on ring one and handle multiple conversations at the same time without busy signals or overloaded reception desks.'
  },
  {
    icon: 'BookOpen',
    title: 'Deep business knowledge',
    description: 'Give accurate answers about services, pricing guidelines, service areas, prerequisites, policies, and operating hours.'
  },
  {
    icon: 'CalendarCheck',
    title: 'Direct calendar scheduling',
    description: 'Book confirmed appointments against real availability through Google Calendar, Calendly, CRM workflows, or custom scheduling systems.'
  },
  {
    icon: 'PhoneForwarded',
    title: 'Intelligent escalation and forwarding',
    description: 'Route urgent, high-value, or emergency calls to the right person while logging routine inquiries for follow-up.'
  },
  {
    icon: 'MessageSquare',
    title: 'Instant SMS and CRM workflows',
    description: 'Send confirmation texts to callers and deliver transcripts, summaries, and action items to your team automatically.'
  }
];

export const virtualComparisonRows = [
  ['Starting Monthly Cost', '$250–$600+ depending on package', '$699/month plus setup'],
  ['Cost Per Extra Minute', '$1.75–$3.00 per minute is common', 'Predictable flat monthly usage'],
  ['Simultaneous Calls', 'Usually one call per available line', 'Multiple concurrent calls'],
  ['24/7/365 Availability', 'Often limited or subject to surcharges', 'Always-on coverage'],
  ['Calendar and Lead Booking', 'Basic message-taking on many plans', 'Calendar and workflow integrations'],
  ['Setup Time', 'Typically 3–7 days of onboarding', 'Standard setup in about 5–10 minutes']
];

export const virtualFaqs = [
  {
    question: 'What is the difference between a traditional virtual receptionist and an AI virtual receptionist?',
    answer: 'A traditional virtual receptionist usually relies on remote human operators who answer calls from scripts and charge by minutes, calls, or service tiers. An AI virtual receptionist uses conversational voice software to answer calls, qualify leads, schedule appointments, route urgent callers, and operate continuously. Human services can be valuable for highly unusual conversations, while AI is usually faster, more consistent, and more economical for repetitive inbound workflows.'
  },
  {
    question: 'Will callers know they are speaking to an AI virtual receptionist?',
    answer: 'Some callers may recognize that they are speaking with an AI, especially if they ask unusual questions or listen closely to the voice. A well-configured system should not misrepresent itself. AutoReception is designed to sound natural, respond quickly, handle interruptions, and provide useful answers without pretending to be a human employee.'
  },
  {
    question: 'Can an AI virtual receptionist transfer emergency or VIP callers to my personal phone?',
    answer: 'Yes. You can define escalation rules for emergencies, urgent service requests, VIP customers, or specific caller answers. The AI can transfer those calls to your mobile, an on-call technician, a department, or a fallback number while logging the interaction and preserving the caller context.'
  },
  {
    question: 'How much can a small business save by switching to an AI virtual receptionist?',
    answer: 'Savings depend on call volume, average call duration, after-hours coverage, and the provider you currently use. A business handling around 150 calls per month may spend approximately $450–$750 with a traditional live answering service before surcharges. A predictable AI plan can reduce that expense substantially while adding 24/7 coverage and automated booking.'
  },
  {
    question: 'How does AutoReception handle spam calls and robocallers without charging me?',
    answer: 'AutoReception can use call-screening rules, caller intent, business knowledge, and routing logic to identify low-value or suspicious interactions. Configure the agent to end obvious spam calls, avoid unnecessary transfers, and reserve human escalation for callers who meet your qualification criteria.'
  },
  {
    question: 'Can I keep my existing phone number and telecom provider?',
    answer: 'Yes. Most businesses can keep their existing number and carrier by forwarding missed calls, after-hours calls, or all inbound calls to an AutoReception number. Number porting may be possible when it makes operational sense, but forwarding is usually the fastest way to test the service.'
  }
];

export const virtualReceptionistSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      name: 'AutoReception AI Virtual Receptionist',
      serviceType: 'Virtual receptionist and AI phone answering service',
      url: 'https://autoreception.net/virtual-receptionist-for-small-business',
      description: 'A 24/7 AI virtual receptionist for small businesses that answers calls, qualifies leads, books appointments, routes urgent callers, and sends call summaries.',
      provider: {
        '@type': 'Organization',
        name: 'AutoReception',
        url: 'https://autoreception.net/'
      },
      areaServed: {
        '@type': 'Place',
        name: 'United States'
      },
      offers: {
        '@type': 'Offer',
        price: '699',
        priceCurrency: 'USD',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: '699',
          priceCurrency: 'USD',
          unitText: 'MONTH'
        },
        url: 'https://autoreception.net/ai-receptionist-for-small-business'
      }
    },
    {
      '@type': 'SoftwareApplication',
      name: 'AutoReception',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url: 'https://autoreception.net/virtual-receptionist-for-small-business',
      featureList: [
        '24/7 AI call answering',
        'Concurrent call handling',
        'Calendar booking',
        'Emergency call transfer',
        'Missed-call SMS automation',
        'Call transcripts and summaries'
      ]
    },
    {
      '@type': 'FAQPage',
      mainEntity: virtualFaqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    }
  ]
};